// Procedural WebAudio SFX + an outdoor ambience MP3 loop. Two internal
// buses (ambient/sfx) that feed the master gain; volumes are user-tunable
// via the editor's Sound panel (see SoundStore).

const OUTSIDE_AMBIANCE_URL = '/assets/audio/outside-ambiance.mp3';
const LAWN_MOWER_URL = '/assets/audio/lawn-mower.mp3';

export interface SoundOverrides {
  bite?: string;
  death?: string;
  pickup?: string;
  /** Day-time outdoor ambience — the default. */
  ambient?: string;
  /** Night ambience. Plays when time-of-day is night AND set. */
  ambientNight?: string;
  /** Windy ambience. Plays during heavy gusts AND set. */
  ambientWind?: string;
  /** Underground ambience. Plays while the player is in nest mode. */
  ambientNest?: string;
  /** Rain ambience. Plays during rain AND set. */
  ambientRain?: string;
  /** Fight ambience. Plays when many ants are in melee combat. */
  ambientFight?: string;
  mower?: string;
}

type AmbientSlot =
  | 'ambient'
  | 'ambientNight'
  | 'ambientWind'
  | 'ambientNest'
  | 'ambientRain'
  | 'ambientFight';

export interface VolumeMix {
  master: number;
  ambient: number;
  sfx: number;
}

export class GameAudio {
  private readonly ctx: AudioContext;
  private readonly master: GainNode;
  private readonly ambientBus: GainNode;
  private readonly sfxBus: GainNode;

  private ambient: {
    osc1: OscillatorNode;
    osc2: OscillatorNode;
    noise: AudioBufferSourceNode;
    gain: GainNode;
  } | null = null;
  private outsideEl: HTMLAudioElement | null = null;

  /** Per-ambient-slot loop element + gain. Loaded lazily when the slot's
   *  URL first becomes set; the runtime picks one slot at a time and
   *  crossfades by ramping the unwanted slots' gains to 0. */
  private ambSlots: Partial<Record<AmbientSlot, {
    el: HTMLAudioElement;
    gain: GainNode;
    src: MediaElementAudioSourceNode | null;
    url: string;
  }>> = {};
  private currentAmbSlot: AmbientSlot | null = null;

  private mowerEl: HTMLAudioElement | null = null;
  private mowerGain: GainNode | null = null;
  private mowerAudioLoaded = false;
  private mowerAudioPending = false;

  private mix: VolumeMix = { master: 1.0, ambient: 0.7, sfx: 0.8 };

  /** URLs of override clips for the built-in slots, set by the world
   *  config at boot. Decoded buffers are cached in `oneShotBuffers` so
   *  per-event playback uses BufferSourceNode (no overlap glitches). */
  private overrides: SoundOverrides = {};
  private oneShotBuffers: Partial<Record<'bite' | 'death' | 'pickup', AudioBuffer>> = {};

  constructor() {
    const Ctor: typeof AudioContext =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.mix.master;
    this.master.connect(this.ctx.destination);

    this.ambientBus = this.ctx.createGain();
    this.ambientBus.gain.value = this.mix.ambient;
    this.ambientBus.connect(this.master);

    this.sfxBus = this.ctx.createGain();
    this.sfxBus.gain.value = this.mix.sfx;
    this.sfxBus.connect(this.master);
  }

  /** Start or stop the looped mower-engine bed. Called each frame with the
   *  mower's `active` flag. Routed through the SFX bus so the Sound panel's
   *  "SFX" slider (and Master / Mute-all) control it. The audio element is
   *  lazy-loaded the first time the mower starts up. */
  setMowerActive(active: boolean): void {
    if (active) {
      if (!this.mowerAudioLoaded && !this.mowerAudioPending) this.loadMowerAudio();
      if (this.mowerEl && this.mowerEl.paused) {
        this.mowerEl.play().catch((err) => console.warn('mower audio play failed', err));
      }
      if (this.mowerGain) {
        const now = this.ctx.currentTime;
        this.mowerGain.gain.cancelScheduledValues(now);
        this.mowerGain.gain.setTargetAtTime(1.0, now, 0.1);
      }
    } else if (this.mowerEl && !this.mowerEl.paused) {
      // Fade out then pause so repeated start/stop cycles don't click.
      if (this.mowerGain) {
        const now = this.ctx.currentTime;
        this.mowerGain.gain.cancelScheduledValues(now);
        this.mowerGain.gain.setTargetAtTime(0, now, 0.2);
      }
      const el = this.mowerEl;
      setTimeout(() => { if (el && el === this.mowerEl) el.pause(); }, 500);
    }
  }

  private loadMowerAudio(): void {
    this.mowerAudioPending = true;
    try {
      const el = new Audio(this.overrides.mower || LAWN_MOWER_URL);
      el.loop = true;
      el.crossOrigin = 'anonymous';
      el.preload = 'auto';
      el.volume = 1.0;
      const gain = this.ctx.createGain();
      gain.gain.value = 0; // ramp in from silent so the first frame doesn't pop
      gain.connect(this.sfxBus);
      const src = this.ctx.createMediaElementSource(el);
      src.connect(gain);
      this.mowerEl = el;
      this.mowerGain = gain;
      this.mowerAudioLoaded = true;
    } catch (err) {
      console.warn('mower audio source failed', err);
    }
  }

  /** Legacy hook from NestMode — swap to the nest ambient when descending,
   *  back to whatever surface ambient the runtime had picked when ascending.
   *  The per-frame setActiveAmbient call from main.ts also handles this,
   *  but keeping this preserves the immediate audio cue on dive/emerge. */
  setOutdoorAmbianceMuted(muted: boolean): void {
    if (muted) this.setActiveAmbient('ambientNest');
    // On unmute we don't pick — main.ts's per-frame logic re-asserts the
    // correct outdoor slot on the next tick.
  }

  /** Apply a fresh set of slot overrides. One-shot URLs are decoded
   *  ahead of time into AudioBuffers so subsequent playback is
   *  zero-latency and supports overlapping events. Ambient + mower
   *  are streamed via HTMLAudio because they're loops. */
  setSoundOverrides(o: SoundOverrides): void {
    this.overrides = { ...o };
    (['bite', 'death', 'pickup'] as const).forEach((slot) => {
      const url = o[slot];
      if (!url) { delete this.oneShotBuffers[slot]; return; }
      void this.decodeOneShot(slot, url);
    });
    // Mower: drop the loaded clip so loadMowerAudio() picks up the new URL.
    if (o.mower && this.mowerEl) {
      this.mowerEl.pause();
      this.mowerEl = null;
      this.mowerGain = null;
      this.mowerAudioLoaded = false;
      this.mowerAudioPending = false;
    }
    // Ambient slots are loaded lazily inside ensureAmbientSlot — nothing
    // to pre-create here. The per-frame setActiveAmbient will pick.
  }

  /** Load (or refresh) the ambient slot's HTMLAudio + gain wiring.
   *  Returns the slot's gain so the caller can ramp it. Returns null if
   *  no URL is configured for the slot. */
  private ensureAmbientSlot(slot: AmbientSlot): GainNode | null {
    const url = this.overrides[slot];
    if (!url) return null;
    const existing = this.ambSlots[slot];
    if (existing && existing.url === url) return existing.gain;
    if (existing) {
      existing.el.pause();
      // Drop the old source so the new URL doesn't double-attach.
    }
    const el = new Audio(url);
    el.loop = true;
    el.crossOrigin = 'anonymous';
    el.preload = 'auto';
    el.volume = 1;
    const gain = this.ctx.createGain();
    gain.gain.value = 0;          // start silent; setActiveAmbient ramps up
    gain.connect(this.ambientBus);
    let src: MediaElementAudioSourceNode | null = null;
    try {
      src = this.ctx.createMediaElementSource(el);
      src.connect(gain);
    } catch (err) {
      console.warn(`[Audio] ambient ${slot} source failed`, err);
    }
    el.play().catch(() => { /* requires gesture */ });
    this.ambSlots[slot] = { el, gain, src, url };
    return gain;
  }

  /** Pick which ambient slot is currently audible. Crossfades by ramping
   *  every other slot to 0 and the chosen one to 1. Pass null to silence
   *  all ambient layers. */
  setActiveAmbient(slot: AmbientSlot | null): void {
    if (this.currentAmbSlot === slot) return;
    const now = this.ctx.currentTime;
    // Ramp ALL existing slots to 0 except the new winner.
    for (const k of Object.keys(this.ambSlots) as AmbientSlot[]) {
      const s = this.ambSlots[k];
      if (!s) continue;
      const target = k === slot ? 1 : 0;
      s.gain.gain.cancelScheduledValues(now);
      s.gain.gain.setTargetAtTime(target, now, 0.4);
    }
    // Lazy-create the chosen slot if not loaded yet.
    if (slot) {
      const g = this.ensureAmbientSlot(slot);
      if (g) {
        g.gain.cancelScheduledValues(now);
        g.gain.setTargetAtTime(1, now, 0.4);
      }
    }
    this.currentAmbSlot = slot;
  }

  private async decodeOneShot(slot: 'bite' | 'death' | 'pickup', url: string): Promise<void> {
    try {
      const res = await fetch(url);
      if (!res.ok) return;
      const arr = await res.arrayBuffer();
      const buf = await this.ctx.decodeAudioData(arr);
      // Only keep it if the override hasn't changed since we started.
      if (this.overrides[slot] === url) this.oneShotBuffers[slot] = buf;
    } catch (err) {
      console.warn(`[Audio] decode failed for ${slot}:`, err);
    }
  }

  private playBuffer(buf: AudioBuffer, vol = 1.0): void {
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const g = this.ctx.createGain();
    g.gain.value = vol;
    src.connect(g).connect(this.sfxBus);
    src.start();
  }

  /** Live-apply editor-tuned levels. Clamped and written straight to the gain nodes. */
  setMix(mix: Partial<VolumeMix>): void {
    this.mix = {
      master: clamp01(mix.master ?? this.mix.master),
      ambient: clamp01(mix.ambient ?? this.mix.ambient),
      sfx: clamp01(mix.sfx ?? this.mix.sfx),
    };
    const now = this.ctx.currentTime;
    this.master.gain.setTargetAtTime(this.mix.master, now, 0.05);
    this.ambientBus.gain.setTargetAtTime(this.mix.ambient, now, 0.05);
    this.sfxBus.gain.setTargetAtTime(this.mix.sfx, now, 0.05);
  }

  attachUnlock(dom: HTMLElement): void {
    const resume = (): void => {
      if (this.ctx.state !== 'running') void this.ctx.resume();
      this.startOutsideAmbiance();
      dom.removeEventListener('pointerdown', resume);
    };
    dom.addEventListener('pointerdown', resume, { once: true });
  }

  /** First user gesture — kick off whichever ambient is currently active.
   *  If the world hasn't set a `ambient` override we still want the legacy
   *  bundled MP3 to play, so we fake an override entry pointing at it. */
  private startOutsideAmbiance(): void {
    if (this.outsideEl) return;
    if (!this.overrides.ambient) {
      // Seed the day slot with the bundled clip so the loop has something
      // to play out of the box.
      this.overrides.ambient = OUTSIDE_AMBIANCE_URL;
    }
    // If main.ts hasn't already chosen a slot, default to day.
    if (this.currentAmbSlot === null) this.setActiveAmbient('ambient');
    // Mark legacy hook as initialised so we don't run this twice. The
    // outsideEl reference itself is no longer used past this point.
    this.outsideEl = this.ambSlots.ambient?.el ?? null;
  }

  playBite(): void {
    if (this.oneShotBuffers.bite) { this.playBuffer(this.oneShotBuffers.bite); return; }
    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(80, now);
    const peak = 0.12;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(peak, now + 0.008);
    g.gain.linearRampToValueAtTime(peak * 0.6, now + 0.020);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.060);
    osc.connect(g).connect(this.sfxBus);
    osc.start(now);
    osc.stop(now + 0.08);
  }

  playDeath(): void {
    if (this.oneShotBuffers.death) { this.playBuffer(this.oneShotBuffers.death); return; }
    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(200, now);
    osc.frequency.exponentialRampToValueAtTime(80, now + 0.25);
    const peak = 0.15;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(peak, now + 0.010);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
    osc.connect(g).connect(this.sfxBus);
    osc.start(now);
    osc.stop(now + 0.28);
  }

  playPickup(): void {
    if (this.oneShotBuffers.pickup) { this.playBuffer(this.oneShotBuffers.pickup); return; }
    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(700, now);
    osc.frequency.exponentialRampToValueAtTime(1200, now + 0.12);
    const peak = 0.08;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(peak, now + 0.010);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
    osc.connect(g).connect(this.sfxBus);
    osc.start(now);
    osc.stop(now + 0.15);
  }

  playAmbient(): void {
    if (this.ambient) return;
    const now = this.ctx.currentTime;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.03, now + 0.8);
    gain.connect(this.ambientBus);

    const osc1 = this.ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.value = 40;
    osc1.connect(gain);

    const osc2 = this.ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = 42;
    osc2.connect(gain);

    const noiseBuf = this.ctx.createBuffer(1, this.ctx.sampleRate * 2, this.ctx.sampleRate);
    const data = noiseBuf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const noise = this.ctx.createBufferSource();
    noise.buffer = noiseBuf;
    noise.loop = true;
    const bp = this.ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 200;
    bp.Q.value = 1;
    noise.connect(bp).connect(gain);

    osc1.start(now);
    osc2.start(now);
    noise.start(now);

    this.ambient = { osc1, osc2, noise, gain };
  }

  stopAmbient(): void {
    if (!this.ambient) return;
    const a = this.ambient;
    this.ambient = null;
    const now = this.ctx.currentTime;
    a.gain.gain.cancelScheduledValues(now);
    a.gain.gain.setValueAtTime(a.gain.gain.value, now);
    a.gain.gain.linearRampToValueAtTime(0, now + 0.3);
    const stopAt = now + 0.35;
    a.osc1.stop(stopAt);
    a.osc2.stop(stopAt);
    a.noise.stop(stopAt);
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
